import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import {
    AuthSessionMessageKey,
    AuthSessionMessageParams,
    LogoutUser,
    StoreState,
} from 'core/store';
import { SnackBarComponent } from '../../page/shared/components/snack-bar/snack-bar.component';
import { throwError } from 'rxjs';

export const WhiteList = [
    'NamesNotFound',
    'AppointmentNotExists',
    'AppointmentUserTasksIsEmpty',
    'ClientIsExists',
    'PeriodicityWarning',
    'CannotEditDates',
    'ErrorQuantityStockItems',
];

const statusesWithOwnSnackbar = [404, 500, 501, 502, 503, 504];

const plainMessageUrls = ['/Admin/Notifications', '/Notifications/My'];

const silentUrls = ['/Notifications/My'];
const silentUrlExceptions = ['/Read'];

@Injectable()
export class ErrorHandler {
    constructor(
        public snackbar: MatSnackBar,
        public translate: TranslateService,
        public store: Store<StoreState>,
    ) {}

    handleError(err: any) {
        if (err.status === 401) {
            this.store.dispatch(
                new LogoutUser({
                    reason: 'unauthorized',
                    ...this.getUnauthorizedMessage(err),
                }),
            );

            return throwError(() => err);
        } else if (this.isSilentError(err)) {
            return throwError(() => err);
        } else {
            try {
                if (err.error instanceof Blob) {
                    err.error.text().then(value => {
                        const isReport = err.url.includes('Reports');
                        const message = JSON.parse(value);
                        if (message && message.ErrorText[0] && !isReport) {
                            this.snackbar.open(message.ErrorText[0], 'ОК', {
                                duration: 5000,
                            });
                        }
                    });
                } else if (err.error?.ErrorCode && err.error?.ErrorText?.length) {
                    const errorCode = Array.isArray(err.error.ErrorCode)
                        ? err.error.ErrorCode[0]
                        : err.error.ErrorCode;

                    if (errorCode && !WhiteList.includes(errorCode)) {
                        if (errorCode !== 'ClientIsExists') {
                            this.snackbar.open(err.error?.ErrorText[0], 'ОК', {
                                duration: 5000,
                            });
                        }
                    }
                } else if (err.error?.ErrorCode && err.error?.Messages?.length) {
                    if (
                        (Array.isArray(err.error?.ErrorCode) &&
                            !WhiteList.includes(err.error?.ErrorCode[0])) ||
                        (!Array.isArray(err.error?.ErrorCode) &&
                            !WhiteList.includes(err.error?.ErrorCode))
                    ) {
                        this.snackbar.openFromComponent(SnackBarComponent, {
                            data: err.error?.Messages.reduce((acc, v) => {
                                if (v) {
                                    return acc?.length ? acc + '<br>' + v : v;
                                } else {
                                    return v;
                                }
                            }, ''),
                            duration: 5000,
                        });
                    }
                } else if (err.error?.error?.message) {
                    this.snackbar.open(err.error?.error?.message, 'ОК', {
                        duration: 5000,
                    });
                } else if (err.error?.errorText) {
                    this.snackbar.open(err.error?.errorText[0], 'ОК', {
                        duration: 5000,
                    });
                } else if (err.error?.ErrorText) {
                    this.snackbar.open(err.error?.ErrorText[0], 'ОК', {
                        duration: 5000,
                    });
                } else if (this.hasPlainMessage(err)) {
                    this.snackbar.open(err.error.message, 'ОК', {
                        duration: 5000,
                    });
                }
                if (err.error?.Value) {
                    this.snackbar.open(err.error?.Value[0], 'ОК', {
                        duration: 5000,
                    });
                }
                if (
                    err.status === 502 ||
                    err.status === 500 ||
                    err.status === 501 ||
                    err.status === 504 ||
                    err.status === 503
                ) {
                    this.snackbar.open(this.translate.instant('errors.500'), 'ОК', {
                        duration: 5000,
                    });
                }
                if (err.status === 404) {
                    this.snackbar.open(this.translate.instant('errors.404'), 'ОК', {
                        duration: 5000,
                    });
                }

                if (typeof err.error === 'string') {
                    this.snackbar.open(err.error, 'ОК', {
                        duration: 5000,
                    });
                }

                return throwError(() => err);
            } catch (e) {
                return throwError(() => err);
            }
        }
    }

    private isSilentError(err: any): boolean {
        return (
            silentUrls.some(url => err?.url?.includes(url)) &&
            !silentUrlExceptions.some(url => err?.url?.includes(url))
        );
    }

    private hasPlainMessage(err: any): boolean {
        return (
            typeof err?.error?.message === 'string' &&
            !!err.error.message &&
            plainMessageUrls.some(url => err.url?.includes(url)) &&
            !statusesWithOwnSnackbar.includes(err.status)
        );
    }

    private getUnauthorizedMessage(err: any): {
        messageKey: AuthSessionMessageKey;
        messageParams?: AuthSessionMessageParams;
    } {
        const reason = this.getSessionTerminationReason(err);

        switch (reason) {
            case 'TerminatedByNewLogin':
                return { messageKey: 'sessionLifecycle.terminatedByNewLogin' };
            case 'LockedByInactivity':
                return this.getInactivityLockedMessage(err);
            case 'TerminatedByAdmin':
                return { messageKey: 'sessionLifecycle.terminatedByAdmin' };
            case 'Expired':
                return { messageKey: 'sessionLifecycle.expired' };
            case 'LoggedOut':
                return { messageKey: 'sessionLifecycle.loggedOut' };
            default:
                return { messageKey: 'sessionLifecycle.genericUnauthorized' };
        }
    }

    private getInactivityLockedMessage(err: any): {
        messageKey: AuthSessionMessageKey;
        messageParams?: AuthSessionMessageParams;
    } {
        const minutes = this.getInactivityMinutes(err);

        if (!minutes) {
            return { messageKey: 'sessionLifecycle.genericUnauthorized' };
        }

        return {
            messageKey: 'sessionLifecycle.inactivityLocked',
            messageParams: { minutes },
        };
    }

    private getInactivityMinutes(err: any): number | null {
        const error = err?.error;
        const value = [
            error?.minutes,
            error?.Minutes,
            error?.idleTimeoutMinutes,
            error?.IdleTimeoutMinutes,
            error?.expiresInMinutes,
            error?.ExpiresInMinutes,
        ].find(candidate => candidate !== undefined && candidate !== null);
        const minutes = Number(value);

        return Number.isFinite(minutes) && minutes > 0 ? minutes : null;
    }

    private getSessionTerminationReason(err: any): string {
        const error = err?.error;
        const candidates = [
            error?.reason,
            error?.Reason,
            error?.sessionTerminationReason,
            error?.SessionTerminationReason,
            error?.sessionStatus,
            error?.SessionStatus,
            error?.ErrorCode,
            error?.error,
            error?.error_description,
            error?.code,
            error?.Code,
        ];

        return this.flatten(candidates)
            .filter(Boolean)
            .map(String)
            .map(value => this.normalizeSessionTerminationReason(value))
            .find(value =>
                [
                    'TerminatedByNewLogin',
                    'LockedByInactivity',
                    'TerminatedByAdmin',
                    'Expired',
                    'LoggedOut',
                ].includes(value),
            );
    }

    private normalizeSessionTerminationReason(reason: string): string {
        const aliases = {
            SESSION_LOCKED_BY_INACTIVITY: 'LockedByInactivity',
        };

        return aliases[reason] || reason;
    }

    private flatten(values: any[]): any[] {
        return values.reduce((acc, value) => acc.concat(value), []);
    }

    // intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    //   return next.handle(req)
    //     .pipe(
    //       catchError(err => {
    //         if (err.status === 401) {
    //           this.store.dispatch(new LogoutUser());
    //         } else if (err.error?.Value) {
    //           this.snackbar.open(err.error?.Value[0], 'ОК', {
    //             duration: 5000
    //           });
    //         } else if (err.error?.ErrorCode) {
    //             if (err.error?.ErrorCode[0] && !WhiteList.includes(err.error?.ErrorCode[0])) {
    //               //               this.snackbar.open(err.error?.ErrorText[0], 'ОК', {
    //                 duration: 5000
    //               });
    //             }
    //         } else {
    //           //           this.snackbar.open(err.error?.ErrorText[0], 'ОК', {
    //             duration: 5000
    //           });
    //         }
    //         return throwError(err);
    //       })
    //     );
    // }
}
